import { useEffect, useMemo, useState, type ComponentType } from "react";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CirclePlus,
  Megaphone,
  Wrench,
  X,
  type LucideProps,
} from "lucide-react";

import { Card } from "@/modern/components/ui/card";
import { DialogContent, DialogTitle } from "@/modern/components/ui/dialog";
import {
  SURFACE_BACK_BTN_CLS,
  SURFACE_ICON_BTN_CLS,
} from "@/modern/components/notification/constants/layout";
import { isNonProduction } from "@/env.jsx";
import { cn } from "@/modern/utils";

import { ChangelogCollapse } from "./ChangelogCollapse";
import { ChangelogEntryRow } from "./ChangelogEntryRow";
import type { ChangelogVersionWithUnread } from "./changelogSelectors";
import type { ChangelogCategory, ChangelogEntry } from "./changelogTypes";

type FilterId = "all" | ChangelogCategory;
type Stage = "summary" | "archive";
type FilterIcon = ComponentType<LucideProps>;

/**
 * Spatial contract — Changelog
 *
 * Radius ladder (3 roles):
 *   shell  rounded-[2rem]
 *   tile   rounded-[1.5rem]
 *   chrome rounded-full
 */

const SHELL_RADIUS_CLS = "sm:rounded-[2rem]";
const TILE_RADIUS_CLS = "rounded-[1.5rem]";
const CONTENT_PAD_CLS = "px-5 sm:px-7";
const EYEBROW_CLS =
  "text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground";

type FlatEntry = {
  entry: ChangelogEntry;
  date: string;
  version: string;
};

function flattenVersions(
  versions: ChangelogVersionWithUnread[]
): FlatEntry[] {
  return versions.flatMap((version) =>
    version.entries.map((entry) => ({
      entry,
      date: version.date,
      version: version.version,
    }))
  );
}

function groupFlatEntries(
  entries: FlatEntry[],
  byVersion: boolean
): { version: string | null; entries: FlatEntry[] }[] {
  if (!byVersion) {
    return [{ version: null, entries }];
  }

  const groups: { version: string; entries: FlatEntry[] }[] = [];
  for (const item of entries) {
    const last = groups[groups.length - 1];
    if (last && last.version === item.version) {
      last.entries.push(item);
    } else {
      groups.push({ version: item.version, entries: [item] });
    }
  }
  return groups;
}

function formatChangelogDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return isoDate;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** Relative label from ISO date — "Today", "2 days ago", "1 year ago". */
function formatRelativeChangelogDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return isoDate;
  const then = new Date(year, month - 1, day);
  if (Number.isNaN(then.getTime())) return isoDate;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round(
    (today.getTime() - then.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.max(1, Math.floor(diffDays / 7));
    return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  }
  if (diffDays < 365) {
    const months = Math.max(1, Math.floor(diffDays / 30));
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }
  const years = Math.max(1, Math.floor(diffDays / 365));
  return years === 1 ? "1 year ago" : `${years} years ago`;
}

/** Newest version open by default; older versions start collapsed. */
function initialCollapsedState(
  groups: { version: string | null }[]
): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  groups.forEach((group, index) => {
    if (!group.version) return;
    next[group.version] = index > 0;
  });
  return next;
}

/**
 * Composition contract — Changelog destination mosaic
 *
 * 1. User question: What's new — and which kind of updates do I want to browse?
 * 2. Mosaic model: Orientation + Nav destinations → Event/list L3
 * 3. L1: interpretation (unread / up to date)
 * 4. L2: Notification-style mosaic (featured beside destinations)
 * 5. Mosaic wire:
 *    [ Featured 2 · row-span-2 ][ All 2 ]
 *    [ ........................ ][ Features 2 ]
 *    [ Improvements 2 ][ Fixes 2 ]
 * 6. List: single-column identity stack + relative dates
 */

interface ChangelogDialogProps {
  open: boolean;
  versions: ChangelogVersionWithUnread[];
  /** Live unseen ids — updates as destinations are viewed. */
  unseenEntryIds?: string[];
  /** Mark entries seen when the user opens their list (counters refresh). */
  onViewEntries?: (entryIds: string[]) => void;
  onClose?: () => void;
  className?: string;
}

export function ChangelogDialogContent({
  open,
  versions,
  unseenEntryIds = [],
  onViewEntries,
  onClose,
  className,
}: ChangelogDialogProps) {
  const [stage, setStage] = useState<Stage>("summary");
  const [filter, setFilter] = useState<FilterId>("all");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  /** Unseen ids at the moment a list opens — keeps New labels while viewing. */
  const [listNewIds, setListNewIds] = useState<string[]>([]);
  const showVersionName = !isNonProduction;
  const unseenSet = useMemo(() => new Set(unseenEntryIds), [unseenEntryIds]);
  const listNewSet = useMemo(() => new Set(listNewIds), [listNewIds]);
  const unreadCount = unseenEntryIds.length;

  const flatEntries = useMemo(() => flattenVersions(versions), [versions]);

  const summaryEntry =
    (unreadCount > 0
      ? flatEntries.find(({ entry }) => unseenSet.has(entry.id))
      : flatEntries[0]) ?? null;

  /** New/unread counts for destination big numbers. */
  const newByFilter = useMemo(() => {
    const base = { all: unreadCount, feature: 0, improvement: 0, fix: 0 };
    for (const { entry } of flatEntries) {
      if (unseenSet.has(entry.id)) {
        base[entry.category] += 1;
      }
    }
    return base;
  }, [flatEntries, unseenSet, unreadCount]);

  /** Catalog totals for destination captions. */
  const totalByFilter = useMemo(() => {
    const base = {
      all: flatEntries.length,
      feature: 0,
      improvement: 0,
      fix: 0,
    };
    for (const { entry } of flatEntries) {
      base[entry.category] += 1;
    }
    return base;
  }, [flatEntries]);

  const visibleEntries = useMemo(() => {
    if (filter === "all") return flatEntries;
    return flatEntries.filter(({ entry }) => entry.category === filter);
  }, [flatEntries, filter]);

  const groupedEntries = useMemo(
    () => groupFlatEntries(visibleEntries, showVersionName),
    [showVersionName, visibleEntries]
  );

  useEffect(() => {
    if (!open) {
      setStage("summary");
      setFilter("all");
      setCollapsed({});
      setListNewIds([]);
    }
  }, [open]);

  const toggleVersion = (versionKey: string) => {
    setCollapsed((prev) => ({
      ...prev,
      [versionKey]: !prev[versionKey],
    }));
  };

  const openDestination = (next: FilterId) => {
    const nextVisible =
      next === "all"
        ? flatEntries
        : flatEntries.filter(({ entry }) => entry.category === next);
    const viewedIds = nextVisible.map(({ entry }) => entry.id);
    // Snapshot New labels for this list visit, then mark seen so counters update.
    setListNewIds(viewedIds.filter((id) => unseenSet.has(id)));
    onViewEntries?.(viewedIds);
    setFilter(next);
    setCollapsed(
      initialCollapsedState(groupFlatEntries(nextVisible, showVersionName))
    );
    setStage("archive");
  };

  const goToSummary = () => {
    setStage("summary");
    setFilter("all");
  };

  const interpretation =
    flatEntries.length === 0
      ? "No product updates to show yet."
      : unreadCount === 0
        ? "You're up to date. Pick a destination to browse."
        : unreadCount === 1
          ? "1 new update — pick a destination to browse."
          : `${unreadCount} new updates — pick a destination to browse.`;

  return (
    <DialogContent
      showCloseButton={false}
      className={cn(
        "flex w-[min(40rem,94vw)] max-w-[40rem] flex-col gap-0 overflow-hidden border-border/60 bg-background p-0 shadow-2xl",
        SHELL_RADIUS_CLS,
        className
      )}
    >
      {stage === "summary" ? (
        <div className={cn(CONTENT_PAD_CLS, "relative pt-5")}>
          <div className="flex items-start gap-3 pr-12">
            <div
              className={cn(
                SURFACE_ICON_BTN_CLS,
                "inline-flex items-center justify-center text-foreground"
              )}
              aria-hidden
            >
              <Megaphone className="size-4" />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <p className={cn("m-0", EYEBROW_CLS)}>Swivu · Updates</p>
              <DialogTitle className="mt-1.5 text-xl font-medium tracking-tight text-foreground">
                What's new
              </DialogTitle>
            </div>
          </div>

          <button
            type="button"
            className={cn(
              SURFACE_ICON_BTN_CLS,
              "absolute right-5 top-5 inline-flex items-center justify-center sm:right-7"
            )}
            aria-label="Close"
            onClick={onClose}
          >
            <X className="size-4" />
          </button>

          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {interpretation}
          </p>
        </div>
      ) : (
        <div className={cn(CONTENT_PAD_CLS, "relative flex items-center pt-5")}>
          <DialogTitle className="sr-only">Updates list</DialogTitle>
          <button
            type="button"
            className={SURFACE_BACK_BTN_CLS}
            aria-label="Back to summary"
            onClick={goToSummary}
          >
            <ChevronLeft className="size-4" aria-hidden />
            Summary
          </button>
          <button
            type="button"
            className={cn(
              SURFACE_ICON_BTN_CLS,
              "absolute right-5 top-5 inline-flex items-center justify-center sm:right-7"
            )}
            aria-label="Close"
            onClick={onClose}
          >
            <X className="size-4" />
          </button>
        </div>
      )}

      <div
        className={cn(
          CONTENT_PAD_CLS,
          "max-h-[min(40rem,72vh)] overflow-y-auto pb-6",
          stage === "summary" ? "mt-5" : "mt-3"
        )}
      >
        {stage === "summary" ? (
          <SummaryStage
            summaryEntry={summaryEntry}
            unseenSet={unseenSet}
            newByFilter={newByFilter}
            totalByFilter={totalByFilter}
            onOpenDestination={openDestination}
          />
        ) : (
          <ArchiveStage
            flatEntries={flatEntries}
            visibleEntries={visibleEntries}
            groupedEntries={groupedEntries}
            showVersionName={showVersionName}
            collapsed={collapsed}
            newSet={listNewSet}
            onToggleVersion={toggleVersion}
          />
        )}
      </div>
    </DialogContent>
  );
}

function DestinationTile({
  eyebrow,
  noun,
  newCount,
  totalCount,
  icon: Icon,
  fieldClass,
  metaClass,
  captionClass,
  countClass,
  span,
  onClick,
}: {
  eyebrow: string;
  /** Plural noun for caption, e.g. "improvements". */
  noun: string;
  /** Big number = new/unread. */
  newCount: number;
  totalCount: number;
  icon: FilterIcon;
  fieldClass: string;
  metaClass: string;
  captionClass: string;
  countClass: string;
  span?: string;
  onClick: () => void;
}) {
  const hasNew = newCount > 0;
  const caption = `${totalCount} total ${noun}, ${newCount} new`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        TILE_RADIUS_CLS,
        fieldClass,
        span,
        "flex min-h-[6.75rem] flex-col justify-between overflow-hidden p-4 text-left shadow-none outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className={cn(
            "min-w-0 truncate text-[10px] font-medium uppercase tracking-[0.18em]",
            metaClass
          )}
        >
          {eyebrow}
        </span>
        <Icon className={cn("size-4 shrink-0 opacity-80", metaClass)} aria-hidden />
      </div>
      <div className="mt-auto pt-2">
        {hasNew ? (
          <p
            className={cn(
              "text-[10px] font-medium uppercase tracking-[0.18em]",
              metaClass
            )}
          >
            New
          </p>
        ) : null}
        <p
          className={cn(
            "font-medium tabular-nums leading-none tracking-tight",
            hasNew ? "mt-1.5 text-3xl" : "text-3xl",
            countClass
          )}
        >
          {newCount}
        </p>
        <p
          className={cn(
            "mt-1.5 text-sm font-medium leading-snug tracking-tight",
            captionClass
          )}
        >
          {caption}
        </p>
      </div>
    </button>
  );
}

function SummaryStage({
  summaryEntry,
  unseenSet,
  newByFilter,
  totalByFilter,
  onOpenDestination,
}: {
  summaryEntry: FlatEntry | null;
  unseenSet: Set<string>;
  newByFilter: Record<FilterId, number>;
  totalByFilter: Record<FilterId, number>;
  onOpenDestination: (filter: FilterId) => void;
}) {
  if (!summaryEntry) {
    return (
      <Card
        variant="mosaic"
        className={cn(
          TILE_RADIUS_CLS,
          "flex min-h-[7rem] flex-col items-center justify-center gap-3 bg-muted/50 py-8 text-center"
        )}
      >
        <div
          className={cn(
            SURFACE_ICON_BTN_CLS,
            "inline-flex items-center justify-center"
          )}
        >
          <Megaphone className="size-4" aria-hidden />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">No updates yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            New product changes will show up here.
          </p>
        </div>
      </Card>
    );
  }

  /**
   * Notification-style mosaic wire:
   * [ Featured 2 · row-span-2 ][ All 2 ]
   * [ ........................ ][ Features 2 ]
   * [ Improvements 2 ][ Fixes 2 ]
   */
  return (
    <div className="grid grid-cols-4 gap-3">
      <button
        type="button"
        onClick={() => onOpenDestination("all")}
        className="col-span-2 row-span-2 min-h-0 rounded-[1.5rem] text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-label="Browse all updates, starting from the latest"
      >
        <ChangelogEntryRow
          entry={summaryEntry.entry}
          relativeDate={formatRelativeChangelogDate(summaryEntry.date)}
          isNew={unseenSet.has(summaryEntry.entry.id)}
          variant="summary"
          className="pointer-events-none h-full min-h-[14rem]"
        />
      </button>

      <DestinationTile
        eyebrow="Complete record"
        noun="updates"
        newCount={newByFilter.all}
        totalCount={totalByFilter.all}
        icon={ChevronRight}
        fieldClass="bg-muted/50 text-foreground"
        metaClass="text-muted-foreground"
        captionClass="text-muted-foreground"
        countClass="text-foreground"
        span="col-span-2"
        onClick={() => onOpenDestination("all")}
      />
      <DestinationTile
        eyebrow="Feature"
        noun="features"
        newCount={newByFilter.feature}
        totalCount={totalByFilter.feature}
        icon={CirclePlus}
        fieldClass="bg-cobalt text-cobalt-foreground"
        metaClass="text-cobalt-foreground/70"
        captionClass="text-cobalt-foreground/80"
        countClass="text-cobalt-foreground"
        span="col-span-2"
        onClick={() => onOpenDestination("feature")}
      />
      <DestinationTile
        eyebrow="Improvement"
        noun="improvements"
        newCount={newByFilter.improvement}
        totalCount={totalByFilter.improvement}
        icon={ArrowUpRight}
        fieldClass="bg-ochre text-ochre-foreground"
        metaClass="text-ochre-foreground/70"
        captionClass="text-ochre-foreground/80"
        countClass="text-ochre-foreground"
        span="col-span-2"
        onClick={() => onOpenDestination("improvement")}
      />
      <DestinationTile
        eyebrow="Fix"
        noun="fixes"
        newCount={newByFilter.fix}
        totalCount={totalByFilter.fix}
        icon={Wrench}
        fieldClass="bg-slate text-slate-foreground"
        metaClass="text-slate-foreground/70"
        captionClass="text-slate-foreground/80"
        countClass="text-slate-foreground"
        span="col-span-2"
        onClick={() => onOpenDestination("fix")}
      />
    </div>
  );
}

function ArchiveStage({
  flatEntries,
  visibleEntries,
  groupedEntries,
  showVersionName,
  collapsed,
  newSet,
  onToggleVersion,
}: {
  flatEntries: FlatEntry[];
  visibleEntries: FlatEntry[];
  groupedEntries: { version: string | null; entries: FlatEntry[] }[];
  showVersionName: boolean;
  collapsed: Record<string, boolean>;
  newSet: Set<string>;
  onToggleVersion: (versionKey: string) => void;
}) {
  if (visibleEntries.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        {flatEntries.length === 0
          ? "No updates yet."
          : "No updates in this filter."}
      </p>
    );
  }

  const renderEntryGrid = (items: FlatEntry[]) => (
    <div className="flex flex-col gap-3">
      {items.map(({ entry, date }) => (
        <ChangelogEntryRow
          key={entry.id}
          entry={entry}
          relativeDate={formatRelativeChangelogDate(date)}
          isNew={newSet.has(entry.id)}
        />
      ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      {groupedEntries.map((group) => {
        const versionKey = group.version ?? "all";
        const useVersionCollapse = Boolean(showVersionName && group.version);
        const isOpen = !collapsed[versionKey];

        if (!useVersionCollapse) {
          return (
            <section key={versionKey}>{renderEntryGrid(group.entries)}</section>
          );
        }

        const versionDate = group.entries[0]?.date;

        return (
          <section key={versionKey} className="flex flex-col">
            <button
              type="button"
              onClick={() => onToggleVersion(versionKey)}
              className={cn(
                TILE_RADIUS_CLS,
                "flex min-h-10 w-full items-center justify-between gap-3 bg-muted/50 px-4 text-left outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              )}
              aria-expanded={isOpen}
            >
              <span className="flex min-w-0 items-center gap-2">
                <ChevronDown
                  className={cn(
                    "size-4 shrink-0 text-muted-foreground transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
                    isOpen ? "rotate-0" : "-rotate-90"
                  )}
                  aria-hidden
                />
                {versionDate ? (
                  <span className="flex min-w-0 flex-col items-start sm:flex-row sm:items-baseline sm:gap-2">
                    <time
                      dateTime={versionDate}
                      className="text-sm font-medium tracking-tight text-foreground"
                    >
                      {formatRelativeChangelogDate(versionDate)}
                    </time>
                    <time
                      dateTime={versionDate}
                      className="text-xs font-medium text-muted-foreground tabular-nums"
                    >
                      {formatChangelogDate(versionDate)}
                    </time>
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                v{versionKey}
              </span>
            </button>

            <ChangelogCollapse open={isOpen}>
              {renderEntryGrid(group.entries)}
            </ChangelogCollapse>
          </section>
        );
      })}
    </div>
  );
}
